import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../axiosInstance";
import ReactPaginate from "react-paginate";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { getEventTypeLabel, LogEventTypeOptions } from "../utils/util";

import {
  IconFileImport,
  IconFileExport,
  IconFileExcel,
  IconPlus,
  IconChevronRight,
  IconChevronLeft,
  IconSearch,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import {
  Box,
  Button,
  Flex,
  LoadingOverlay,
  Menu,
  ScrollArea,
  Select,
  Table,
  TableThead,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { boardDateFormat } from "../utils/util";
import { MENU_MAP, MenuOptions } from "../components/MenuData";
import { useModal } from "../contexts/useModal";
import { ActionLogDetail } from "../components/Modal";
import { useDynamicScroll } from "../utils/useDynamicScroll";
import { ROW_HOVER_BG, BUTTON_WORD } from "../constants/colors";
const isDev = process.env.REACT_APP_IS_DEV === "true";

const ActionLog: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [length, setLength] = useState(50);
  const [pageNo, setPageNo] = useState(1);
  const [pageTotal, setPageTotal] = useState(1);
  const _isSearch = useRef(false);
  const [isSearch, setIsSearch] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const todayStr = dayjs().format("YYYY-MM-DD");
  const yesterdayStr = dayjs().subtract(1, "day").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState<string>(yesterdayStr);
  const [endDate, setEndDate] = useState<string>(todayStr);
  const [eventType, setEventType] = useState<string>(
    LogEventTypeOptions[0]?.value || ""
  );
  const [menu, setMenu] = useState<string>(MenuOptions[0]?.value || "");
  const [userName, setUserName] = useState<string>("");
  const [ip, setIp] = useState<string>("");
  const [serialNo, setSerialNo] = useState<string>("");
  const [action, setAction] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] =
    useState<boolean>(false);
  const { showModal } = useModal();

  // 내용 클릭 핸들러
  const handleContentClick = async (row: any) => {
    await showModal({ component: ActionLogDetail, props: { row } });
  };

  const theme = useMantineTheme();

  // 동적 스크롤 제어 (최소 높이 500px)
  useDynamicScroll({ minHeight: 500 });

  const lock1 = useRef(false);
  useEffect(() => {
    if (!lock1.current) {
      lock1.current = true;
      return;
    }
    if (pageNo !== 1) {
      setPageNo(1);
    } else {
      getList();
    }
  }, [length]);

  useEffect(() => {
    getList();
  }, [pageNo]);

  const getList = () => {
    if (isDev) return;
    if (_isSearch.current) return;
    _isSearch.current = true;
    setIsSearch(true);
    axiosInstance
      .get("/api/v1/logs/list", {
        params: {
          currentPageNo: pageNo,
          recordsPerPage: length,
          startDate: `${startDate} 00:00:00`,
          endDate: `${endDate} 23:59:59`,
          eventType: eventType || undefined,
          menu: menu || undefined,
          userName: userName || undefined,
          ip: ip || undefined,
          serialNo: serialNo || undefined,
          action: action || undefined,
          message: message || undefined,
        },
      })
      .then((res) => {
        if (res.status === 200) {
          const data = res.data as any;
          const r = data.data;
          const p = r.paging;
          setData(r.list || []);
          setTotal(p.totalRecordCount);
          setPageTotal(Math.ceil(p.totalRecordCount / length));
        } else {
          setData([]);
        }
      })
      .catch((err) => {
        toast.error("조회에 실패하였습니다.");
      })
      .finally(() => {
        _isSearch.current = false;
        setIsSearch(false);
      });
  };

  return (
    <div
      style={{
        height: "100%",
        background: "#fafbfc",
        borderRadius: "12px",
        padding: "0.75rem 1.5rem",
        display: "flex",
        flexDirection: "column",
        minHeight: "500px", // 최소 높이 보장
      }}
    >
      <Flex align={"center"} justify={"space-between"} pb={"1rem"}>
        <Box fz={"1.75rem"} fw={700}>
          {menuTitle}
        </Box>
      </Flex>

      {/* 기본 검색 영역 */}
      <Flex align={"flex-end"} gap={"xs"} pb={"1rem"}>
        <DateInput
          label="검색 시작"
          placeholder="날짜를 선택하세요"
          valueFormat="YYYY-MM-DD"
          value={startDate}
          onChange={(val) => {
            setStartDate(val || todayStr);
          }}
          nextIcon={<IconChevronRight size={16} />}
          previousIcon={<IconChevronLeft size={16} />}
          renderDay={(dateStr) => {
            const dateObj = new Date(dateStr); // 문자열 → Date 객체
            const day = dateObj.getDay();

            const style: React.CSSProperties = {
              color: day === 0 ? "red" : day === 6 ? "blue" : undefined,
            };

            return <div style={style}>{dateObj.getDate()}</div>;
          }}
        />
        <DateInput
          label="검색 종료"
          placeholder="날짜를 선택하세요"
          valueFormat="YYYY-MM-DD"
          value={endDate}
          onChange={(val) => {
            let newEnd = val || todayStr;
            if (new Date(newEnd) < new Date(startDate)) {
              newEnd = startDate;
            }
            setEndDate(newEnd);
          }}
          nextIcon={<IconChevronRight size={16} />}
          previousIcon={<IconChevronLeft size={16} />}
          renderDay={(dateStr) => {
            const dateObj = new Date(dateStr); // 문자열 → Date 객체
            const day = dateObj.getDay();

            const style: React.CSSProperties = {
              color: day === 0 ? "red" : day === 6 ? "blue" : undefined,
            };

            return <div style={style}>{dateObj.getDate()}</div>;
          }}
        />
        <Select
          label="분류"
          data={LogEventTypeOptions}
          defaultValue={LogEventTypeOptions[0]?.value || ""}
          value={eventType}
          allowDeselect={false}
          onChange={(val) => {
            setEventType(val || "");
          }}
        />
        <TextInput
          label="사용자"
          placeholder="사용자를 입력하세요"
          value={userName}
          onChange={(e) => {
            setUserName(e.target.value);
          }}
        />
        <Button
          leftSection={<IconSearch size={20} stroke={1.5} />}
          onClick={getList}
          disabled={isSearch}
          variant="filled"
          style={{ backgroundColor: "#8FC0CF", color: "#fff" }}
        >
          검색
        </Button>
        <Button
          leftSection={
            isAdvancedSearchOpen ? (
              <IconChevronUp size={16} />
            ) : (
              <IconChevronDown size={16} />
            )
          }
          onClick={() => setIsAdvancedSearchOpen(!isAdvancedSearchOpen)}
          variant="outline"
          color="gray"
        >
          {isAdvancedSearchOpen ? "고급검색 닫기" : "고급검색"}
        </Button>
      </Flex>

      {/* 고급 검색 영역 (아코디언) */}
      {isAdvancedSearchOpen && (
        <Flex align={"flex-end"} gap={"xs"} pb={"1rem"} wrap="wrap">
          <Select
            label="메뉴"
            data={MenuOptions}
            defaultValue={MenuOptions[0]?.value || ""}
            value={menu}
            allowDeselect={false}
            onChange={(val) => {
              setMenu(val || "");
            }}
          />
          <TextInput
            label="IP"
            placeholder="IP를 입력하세요"
            value={ip}
            onChange={(e) => {
              setIp(e.target.value);
            }}
          />
          <TextInput
            label="안드로이드 S/N"
            placeholder="안드로이드 S/N를 입력하세요"
            value={serialNo}
            onChange={(e) => {
              setSerialNo(e.target.value);
            }}
          />
          <TextInput
            label="행동"
            placeholder="행동을 입력하세요"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
            }}
          />
          <TextInput
            label="내용"
            placeholder="내용을 입력하세요"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
            }}
          />
        </Flex>
      )}
      <div
        style={{
          flex: 1, // 남은 공간을 모두 차지
          minHeight: "300px", // 최소 높이 보장
          overflow: "auto",
          scrollbarGutter: "stable",
          boxSizing: "border-box",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          borderRadius: 8,
          background: "#fff",
          position: "relative",
        }}
      >
        <LoadingOverlay
          visible={isSearch}
          zIndex={1000}
          overlayProps={{ radius: "sm", bg: "rgba(0,0,0,0.3)" }}
          loaderProps={{ color: "pink", type: "bars" }}
        />
        <Table style={{ tableLayout: "fixed", width: "100%" }}>
          <Table.Thead>
            <Table.Tr bg={"#f5f6fa"} ta={"left"} pos={"sticky"} top={0}>
              <Table.Th p={12} w={160} pos={"sticky"} top={0}>
                일시
              </Table.Th>
              <Table.Th p={12} w={120} pos={"sticky"} top={0}>
                분류
              </Table.Th>
              <Table.Th p={12} w={140} pos={"sticky"} top={0}>
                메뉴
              </Table.Th>
              <Table.Th p={12} w={120} pos={"sticky"} top={0}>
                사용자
              </Table.Th>
              <Table.Th p={12} w={120} pos={"sticky"} top={0}>
                IP
              </Table.Th>
              <Table.Th p={12} w={180} pos={"sticky"} top={0}>
                안드로이드 S/N
              </Table.Th>
              <Table.Th p={12} w={250} pos={"sticky"} top={0}>
                행동
              </Table.Th>
              <Table.Th p={12} pos={"sticky"} top={0} style={{ width: "auto" }}>
                내용
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.map((row, idx) => {
              return (
                <Table.Tr
                  key={row.id || idx}
                  bg={hoveredRow === idx ? ROW_HOVER_BG : undefined}
                  onMouseEnter={() => setHoveredRow(idx)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <Table.Td p={"4px 12px"}>
                    {boardDateFormat(row.createdDate)}
                  </Table.Td>
                  <Table.Td p={"4px 12px"}>
                    {getEventTypeLabel(row.eventType)}
                  </Table.Td>
                  <Table.Td p={"4px 12px"}>{MENU_MAP[row.menuUrl]}</Table.Td>
                  <Table.Td p={"4px 12px"}>{row.userName}</Table.Td>
                  <Table.Td p={"4px 12px"}>{row.ip}</Table.Td>
                  <Table.Td p={"4px 12px"}>{row.serialNo}</Table.Td>
                  <Table.Td p={"4px 12px"}>{row.action}</Table.Td>
                  <Table.Td
                    p={"4px 12px"}
                    style={{
                      width: "auto",
                      minWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      color: "#1976d2",
                      maxWidth: "none",
                    }}
                    title="클릭하여 상세 내용 보기"
                    onClick={() => handleContentClick(row)}
                  >
                    {row.message}
                  </Table.Td>
                </Table.Tr>
              );
            })}
            {/*Array.from({ length: 30 }).map((_, i) => (
                      <Table.Tr key={i} style={{ height: 30,}}>
                        <td colSpan={5}>asd</td>
                      </Table.Tr>
                    ))*/}
          </Table.Tbody>
        </Table>
      </div>
      <Flex pt={16} align={"flex-end"}>
        <Flex mr={"auto"} align={"center"}>
          <span style={{ marginRight: "1rem" }}>총 {total}개</span>
          <select
            onChange={(e) => {
              const opt_list = [50, 100, 200];
              setLength(opt_list[e.target.options.selectedIndex]);
              //getPageCount()
            }}
            style={{
              height: "31px",
              width: "50px",
              textAlign: "center",
              fontSize: "1rem",
            }}
          >
            <option>50</option>
            <option>100</option>
            <option>200</option>
          </select>
          <span>개씩 보기</span>
        </Flex>
        <ReactPaginate
          previousLabel="<"
          nextLabel=">"
          breakLabel="..."
          pageCount={pageTotal}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={(e) => {
            setPageNo(e.selected + 1);
          }}
          forcePage={pageNo - 1}
          containerClassName="pagination"
          activeClassName="active"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          nextClassName="page-item"
          previousLinkClassName="page-link"
          nextLinkClassName="page-link"
          breakClassName="page-item"
          breakLinkClassName="page-link"
        />
      </Flex>
    </div>
  );
};

export default ActionLog;
